/**
 * CoherencePreviewPlayer — inline 30-second sample button on the landing
 * page hero, just under the social-proof rotator.
 *
 * Why this exists: every CTA above this point routes the visitor to a fresh
 * page (`/app` or `/app?coherence=1`) where they have to mount the audio
 * engine, click through a safety gate, and grant a user gesture before
 * actually hearing anything. That's a ~5-second journey with two abandon
 * cliffs (page-load wait, autoplay-policy block). This component collapses
 * the whole funnel to a single click → audible heartbeat right on the
 * landing page, no signup, no navigation.
 *
 * Asset: /coherence-preview-30s.mp3 — 30 seconds of the same heartbeat
 * sample at 60 BPM layered with a soft 10 Hz alpha binaural pad
 * (220 Hz left, 230 Hz right). Pre-baked so there's no Web Audio
 * complexity at runtime — just a plain HTMLAudioElement which has zero
 * issues with autoplay policy as long as the play() is in a click handler.
 *
 * UX choices:
 *   - Pill button matching the rose/pink accent of the Coherence stack.
 *   - Live progress bar that fills as the preview plays.
 *   - Toggles between "▶ Hear 30 seconds of Coherence" and "■ Stop preview".
 *   - Auto-resets at the end of playback so the button is ready for a
 *     second listen without manual reset.
 *   - `preload="none"` on the audio element so the 705 KB file isn't
 *     downloaded for visitors who never click — preserves the LCP budget.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Play, Square } from 'lucide-react';

export const CoherencePreviewPlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [error, setError] = useState(null);

  // Wire up the audio element's events once. We can't put these on the JSX
  // <audio> directly because some are tricky to remove cleanly across
  // Strict Mode double-mount, and we want a stable cleanup.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => {
      if (audio.duration > 0) setProgress(audio.currentTime / audio.duration);
    };
    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
      audio.currentTime = 0;
    };
    const onError = () => {
      setError('Could not load preview');
      setPlaying(false);
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, []);

  const handleClick = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);

    if (playing) {
      audio.pause();
      audio.currentTime = 0;
      setProgress(0);
      setPlaying(false);
      return;
    }

    try {
      // play() returns a Promise that rejects on autoplay block. We're in a
      // click handler so this should always succeed, but defensive try/catch
      // covers Safari iOS + odd corporate-MDM browsers.
      await audio.play();
      setPlaying(true);
    } catch (e) {
      setError('Playback blocked — try again');
    }
  };

  return (
    <div
      className="flex flex-col items-center mb-12"
      data-testid="coherence-preview-root"
    >
      {/* preload=none keeps the 705KB MP3 out of the LCP budget for
          visitors who never click. */}
      <audio
        ref={audioRef}
        src="/coherence-preview-30s.mp3"
        preload="none"
        data-testid="coherence-preview-audio"
      />

      <button
        type="button"
        onClick={handleClick}
        data-testid="coherence-preview-button"
        aria-pressed={playing}
        className="group relative inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-rose-300/30 bg-rose-500/[0.06] hover:border-rose-300/60 hover:bg-rose-500/[0.10] transition-all text-rose-100/90 text-sm font-medium overflow-hidden"
      >
        {/* Progress fill — sits behind the icon/text, scales horizontally
            with current playback progress. Subtle so the text remains
            legible across the whole 30 seconds. */}
        {playing && (
          <span
            data-testid="coherence-preview-progress"
            className="absolute inset-y-0 left-0 bg-rose-400/15 transition-[width] duration-100 ease-linear pointer-events-none"
            style={{ width: `${progress * 100}%` }}
            aria-hidden="true"
          />
        )}

        <span className="relative z-10 flex items-center gap-2.5">
          {playing ? (
            <>
              <Square className="w-3.5 h-3.5 fill-current" />
              <span>Stop preview</span>
              <span className="text-rose-200/50 text-xs tabular-nums">
                {Math.max(0, 30 - Math.round(progress * 30))}s
              </span>
            </>
          ) : (
            <>
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>Hear 30 seconds of Coherence</span>
            </>
          )}
        </span>
      </button>

      {error && (
        <p className="mt-2 text-rose-300/70 text-xs" data-testid="coherence-preview-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default CoherencePreviewPlayer;
